import { useState, useEffect, useRef } from 'react';
import { Header } from '../components/header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Calendar,
  Clock,
  Briefcase,
  Coffee,
  Zap,
  ClipboardList,
  Navigation,
} from 'lucide-react';
import { useLanguage } from '../context/language-context';
import { useAuth } from 'react-oidc-context';

// Helper function to format time as HH:MM:SS
function formatTime(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(
    2,
    '0'
  )}:${String(secs).padStart(2, '0')}`;
}

// Helper function to format time as HH:MM
function formatTimeShort(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(
    2,
    '0'
  )}`;
}

// Helper function to get work time color based on duration
function getWorkTimeColor(seconds: number): string {
  if (seconds < 7200) {
    // Less than 2 hours
    return 'bg-red-500';
  } else if (seconds < 14400) {
    // Between 2-4 hours
    return 'bg-yellow-500';
  } else {
    // Greater than 4 hours
    return 'bg-green-500';
  }
}

// Base URL from environment variables
const BASE_URL = import.meta.env.VITE_BASE_URL;

const TrackPage = () => {
  const auth = useAuth();
  // Use the language context instead of local state
  const { language } = useLanguage();

  // Use refs to track the actual time values
  const connectionTimeRef = useRef(1417); // 23:37 in seconds
  const breakTimeRef = useRef(92); // 01:32 in seconds

  // State for UI updates
  const [connectionTime, setConnectionTime] = useState(
    connectionTimeRef.current
  );
  const [breakTime, setBreakTime] = useState(breakTimeRef.current);
  const [isWorking, setIsWorking] = useState(true);
  const [startTime] = useState('08:37:41');

  // State for inspection data
  const [assignedInspections, setAssignedInspections] = useState(0);
  const [completedInspections, setCompletedInspections] = useState(0);
  const [inProgressInspections, setInProgressInspections] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Target values - updated as per requirements
  const workHoursTarget = 21600; // 6 hours in seconds
  const breakHoursTarget = 7200; // 2 hours in seconds
  const totalHoursTarget = workHoursTarget + breakHoursTarget; // 8 hours total
  const pointsTarget = 10.0;
  const achieved = connectionTime;
  const points = 0.7;

  // Calculate percentages
  const workHoursPercentage = Math.round(
    (achieved / workHoursTarget) * 100
  );
  const breakHoursPercentage = Math.round(
    (breakTime / breakHoursTarget) * 100
  );
  const totalHoursPercentage = Math.round(
    ((achieved + breakTime) / totalHoursTarget) * 100
  );
  const pointsPercentage = Math.round((points / pointsTarget) * 100);

  // Get work time color based on duration
  const workTimeColor = getWorkTimeColor(connectionTime);

  // Timer effect with debugging
  useEffect(() => {
    console.log('Timer effect running, isWorking:', isWorking);

    const timer = setInterval(() => {
      if (isWorking) {
        connectionTimeRef.current += 1;
        setConnectionTime(connectionTimeRef.current);
        console.log(
          'Incrementing connection time:',
          connectionTimeRef.current
        );
      } else {
        breakTimeRef.current += 1;
        setBreakTime(breakTimeRef.current);
        console.log('Incrementing break time:', breakTimeRef.current);
      }
    }, 1000);

    return () => {
      console.log('Clearing timer');
      clearInterval(timer);
    };
  }, [isWorking]);

  // Fetch inspection data from API
  useEffect(() => {
    const fetchInspectionData = async () => {
      if (!auth?.user?.access_token) return;

      try {
        setIsLoading(true);
        setError(null);

        // Fetch assigned inspections (task_status_id = 6)
        const assignedUrl = `${BASE_URL}/jsonapi/transform/task_list?sortBy=created_at&sortOrder=desc&page=1&pageSize=100&condition=${encodeURIComponent(
          JSON.stringify({ rules: [{ operator: 'eq', field: 'task_status_id', value: '6' }] })
        )}`;

        // Fetch completed inspections (task_status_id = 15)
        const completedUrl = `${BASE_URL}/jsonapi/transform/task_list?sortBy=created_at&sortOrder=desc&page=1&pageSize=100&condition=${encodeURIComponent(
          JSON.stringify({ rules: [{ operator: 'eq', field: 'task_status_id', value: '15' }] })
        )}`;

        // Fetch in-progress inspections (task_status_id = 14)
        const inProgressUrl = `${BASE_URL}/jsonapi/transform/task_list?sortBy=created_at&sortOrder=desc&page=1&pageSize=100&condition=${encodeURIComponent(
          JSON.stringify({ rules: [{ operator: 'eq', field: 'task_status_id', value: '14' }] })
        )}`;

        const [assignedResponse, completedResponse, inProgressResponse] = await Promise.all([
          fetch(assignedUrl, {
            headers: {
              Authorization: `Bearer ${auth.user.access_token}`,
            },
          }),
          fetch(completedUrl, {
            headers: {
              Authorization: `Bearer ${auth.user.access_token}`,
            },
          }),
          fetch(inProgressUrl, {
            headers: {
              Authorization: `Bearer ${auth.user.access_token}`,
            },
          })
        ]);

        if (!assignedResponse.ok || !completedResponse.ok || !inProgressResponse.ok) {
          throw new Error('Failed to fetch inspection data');
        }

        const assignedData = await assignedResponse.json();
        const completedData = await completedResponse.json();
        const inProgressData = await inProgressResponse.json();

        // Update state with counts
        setAssignedInspections(assignedData.data.length);
        setCompletedInspections(completedData.data.length);
        setInProgressInspections(inProgressData.data.length);

        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching inspection data:', error);
        setError('Failed to fetch inspection data');
        setIsLoading(false);
      }
    };

    fetchInspectionData();
  }, [auth?.user?.access_token]);

  const toggleWorkingStatus = () => {
    console.log('Toggling from', isWorking, 'to', !isWorking);
    setIsWorking((prev) => !prev);
  };

  // Generate hourly tick marks for work hours (6 hours)
  const workHourlyTicks = Array.from(
    { length: 6 },
    (_, i) => ((i + 1) / 6) * 100
  );

  // Generate hourly tick marks for break hours (2 hours)
  const breakHourlyTicks = Array.from(
    { length: 2 },
    (_, i) => ((i + 1) / 2) * 100
  );

  return (
    <div
      className="flex flex-col min-h-screen"
      dir={language === 'ar' ? 'rtl' : 'ltr'}
    >
      <Header
        title={language === 'en' ? 'Track' : 'تتبع'}
        isRTL={language === 'ar'}
      />
      <div className="relative top-[5rem] flex-1 p-4 space-y-4">
        {/* Today's Summary */}
        <div className="relative">
          <Card className="rounded-2xl shadow-lg overflow-hidden border border-gray-200">
            <div className="p-4">
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-gray-800" />
                  <h2 className="text-lg font-medium text-gray-800">
                    {language === 'en'
                      ? "Today's Summary"
                      : 'ملخص اليوم'}
                  </h2>
                </div>
                <span className="text-gray-800">03/11/2025</span>
              </div>

              {/* Three-column layout for summary items */}
              <div className="grid grid-cols-3 gap-2">
                {/* Inspections - Larger Display */}
                <div className="bg-white/70 rounded-lg p-2">
                  <div className="flex items-center gap-2 mb-2">
                    <ClipboardList className="w-6 h-6 text-gray-800" />
                    <h3 className="text-xs font-medium text-gray-700">
                      {language === 'en'
                        ? 'Inspections'
                        : 'عمليات التفتيش'}
                    </h3>
                  </div>
                  <div className="flex justify-center">
                    <div className="flex flex-col items-center">
                      {isLoading ? (
                        <span className="text-2xl font-bold text-gray-800">
                          Loading...
                        </span>
                      ) : error ? (
                        <span className="text-sm text-red-500">
                          Error loading data
                        </span>
                      ) : (
                        <>
                          <span className="text-2xl font-bold text-gray-800">
                            {completedInspections}/{assignedInspections + completedInspections}
                          </span>
                          <span className="text-xs text-[#b89256]">
                            {inProgressInspections} in progress
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Work Hours with Circular Progress */}
                <div className="bg-white/70 rounded-lg p-2">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-6 h-6 text-gray-800" />
                    <h3 className="text-xs font-medium text-gray-700">
                      {language === 'en'
                        ? 'Work Hours'
                        : 'ساعات العمل'}
                    </h3>
                  </div>
                  <div className="flex justify-center">
                    <div className="relative w-12 h-12">
                      <svg
                        className="w-full h-full -rotate-90"
                        viewBox="0 0 36 36"
                      >
                        <circle
                          cx="18"
                          cy="18"
                          r="16"
                          fill="none"
                          stroke="rgba(150,111,46,0.2)"
                          strokeWidth="3"
                        />
                        <circle
                          cx="18"
                          cy="18"
                          r="16"
                          fill="none"
                          stroke="var(--color-gold-500)"
                          strokeWidth="3"
                          strokeDasharray={`${workHoursPercentage}, 100`}
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-xs font-bold text-gray-800">
                          {workHoursPercentage}%
                        </span>
                        <span className="text-[9px] text-gray-700">
                          {formatTimeShort(connectionTime)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Points with Circular Progress */}
                <div className="bg-white/70 rounded-lg p-2">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="w-6 h-6 text-gray-800" />
                    <h3 className="text-xs font-medium text-gray-700">
                      {language === 'en' ? 'Points' : 'النقاط'}
                    </h3>
                  </div>
                  <div className="flex justify-center">
                    <div className="relative w-12 h-12">
                      <svg
                        className="w-full h-full -rotate-90"
                        viewBox="0 0 36 36"
                      >
                        <circle
                          cx="18"
                          cy="18"
                          r="16"
                          fill="none"
                          stroke="rgba(150,111,46,0.2)"
                          strokeWidth="3"
                        />
                        <circle
                          cx="18"
                          cy="18"
                          r="16"
                          fill="none"
                          stroke="#966f2e"
                          strokeWidth="3"
                          strokeDasharray={`${pointsPercentage}, 100`}
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-xs font-bold text-gray-800">
                          {pointsPercentage}%
                        </span>
                        <span className="text-[9px] text-gray-700">
                          {points}/{pointsTarget}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Connected and Break Status - Side by Side */}
        <Card className="rounded-2xl shadow-lg overflow-hidden border border-gray-200">
          <CardContent className="p-0">
            <div className="flex relative">
              {/* Connected Status - Left Side */}
              <div className={`flex-1 p-4 transition-all`}>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <div
                      className={`${
                        isWorking
                          ? 'p-2 rounded-full'
                          : 'p-2 rounded-full'
                      }`}
                    >
                      <div
                        className={`w-3 h-3 rounded-full ${
                          isWorking
                            ? 'animate-pulse bg-green-500'
                            : 'bg-gray-400'
                        }`}
                      />{' '}
                    </div>
                    <h3 className="font-medium">
                      {language === 'en' ? 'Connected' : 'متصل'}
                    </h3>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Clock className="w-4 h-4" />
                    <span>
                      {language === 'en' ? 'Start Time' : 'وقت البدء'}
                    </span>
                  </div>
                  <div className="text-xl font-bold text-[var(--color-gold-500)]">
                    {startTime}
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    <Zap className="w-4 h-4 text-[var(--color-gold-500)]" />
                    <span className="text-lg font-bold text-[var(--color-gold-500)]">
                      {formatTime(connectionTime)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Work/Break Toggle Button - Center */}
              <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                <Button
                  className={`rounded-full h-12 w-12 shadow-lg ${
                    isWorking
                      ? 'bg-[var(--color-gold-500)] hover:opacity-90'
                      : 'bg-opacity-70 bg-[var(--color-gold-500)] hover:bg-[var(--color-gold-500)]'
                  }`}
                  onClick={toggleWorkingStatus}
                >
                  {isWorking ? (
                    <Coffee className="h-10 w-10 text-white" />
                  ) : (
                    <Briefcase className="h-10 w-10 text-white" />
                  )}
                </Button>
              </div>

              {/* Break Status - Right Side */}
              <div className={`flex-1 p-4 transition-all`}>
                <div className={`space-y-1 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                  <div className={`flex items-center gap-2 ${language === 'ar' ? 'justify-start' : 'justify-end'}`}>
                    <h3 className="font-medium">
                      {language === 'en'
                        ? 'Break Status'
                        : 'حالة الاستراحة'}
                    </h3>
                    <div
                      className={`${
                        !isWorking
                          ? 'p-2 rounded-full'
                          : 'p-2 rounded-full'
                      }`}
                    >
                      <Coffee
                        className={`w-5 h-5 ${
                          !isWorking
                            ? 'text-red-500'
                            : 'text-gray-400'
                        }`}
                      />
                    </div>
                  </div>
                  <div className={`flex items-center gap-2 ${language === 'ar' ? 'justify-start' : 'justify-end'} text-gray-500`}>
                    <span>
                      {isWorking
                        ? language === 'en'
                          ? 'Not on break'
                          : 'ليس في استراحة'
                        : language === 'en'
                        ? 'On break'
                        : 'في استراحة'}
                    </span>
                  </div>
                  <div className="text-xl font-bold text-[var(--color-gold-500)]">
                    {formatTime(breakTime)}
                  </div>
                  <div className={`flex items-center gap-1 ${language === 'ar' ? 'justify-start' : 'justify-end'} mt-1`}>
                    <span className="text-sm text-gray-500">
                      {language === 'en'
                        ? 'Total break time today'
                        : 'إجمالي وقت الاستراحة اليوم'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Today's Performance with Conditional Coloring */}
        <Card className="rounded-2xl shadow-lg overflow-hidden border border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-[var(--color-gold-500)]" />
                <span className="font-medium">
                  {language === 'en'
                    ? "Today's Performance"
                    : 'أداء اليوم'}
                </span>
              </div>

              {/* Points Circular Progress */}
              <div className="flex items-center gap-3">
                <div className={language === 'ar' ? 'text-left' : 'text-right'}>
                  <div className="text-xs text-gray-500">
                    {language === 'en' ? 'Points' : 'النقاط'}
                  </div>
                  <div className="text-[#966f2e] font-bold text-sm">
                    {points}/{pointsTarget.toFixed(1)}
                  </div>
                </div>
                <div className="relative w-10 h-10">
                  <svg
                    className="w-full h-full -rotate-90"
                    viewBox="0 0 36 36"
                  >
                    <circle
                      cx="18"
                      cy="18"
                      r="16"
                      fill="none"
                      stroke="#e6d7b8"
                      strokeWidth="3"
                    />
                    <circle
                      cx="18"
                      cy="18"
                      r="16"
                      fill="none"
                      stroke="#966f2e"
                      strokeWidth="3"
                      strokeDasharray={`${pointsPercentage}, 100`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xs font-bold text-[#966f2e]">
                      {pointsPercentage}%
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Work Hours Progress - Conditional Coloring */}
            <div className="mb-3">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-medium text-[#966f2e]">
                  {language === 'en'
                    ? 'Work Hours (6h)'
                    : 'ساعات العمل (6 ساعات)'}
                </span>
                <span className="text-xs text-gray-500">
                  {formatTimeShort(connectionTime)} /{' '}
                  {formatTimeShort(workHoursTarget)}
                </span>
              </div>
              <div className="relative h-4 bg-[#f9fafb] rounded-lg overflow-hidden">
                {/* Hourly tick marks for work hours */}
                {workHourlyTicks.map((tick, index) => (
                  <div
                    key={index}
                    className="absolute top-0 bottom-0 w-px bg-white/50"
                    style={{ [language === 'ar' ? 'right' : 'left']: `${tick}%` }}
                  >
                    <div className={`absolute -top-4 ${language === 'ar' ? 'right-1/2 translate-x-1/2' : 'left-1/2 -translate-x-1/2'} transform text-[8px] text-[#966f2e]`}>
                      {index + 1}h
                    </div>
                  </div>
                ))}

                {/* Work progress bar with conditional color */}
                <div
                  className={`absolute top-0 h-full transition-all duration-300 ${workTimeColor} ${language === 'ar' ? 'right-0' : 'left-0'}`}
                  style={{ width: `${workHoursPercentage}%` }}
                />
              </div>
            </div>

            {/* Break Hours Progress - Sapphire Blue */}
            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-medium text-[#966f2e]">
                  {language === 'en'
                    ? 'Break Hours (2h)'
                    : 'ساعات الاستراحة (2 ساعة)'}
                </span>
                <span className="text-xs text-gray-500">
                  {formatTimeShort(breakTime)} /{' '}
                  {formatTimeShort(breakHoursTarget)}
                </span>
              </div>
              <div className="relative h-4 bg-[#f9fafb] rounded-lg overflow-hidden">
                {/* Hourly tick marks for break hours */}
                {breakHourlyTicks.map((tick, index) => (
                  <div
                    key={index}
                    className="absolute top-0 bottom-0 w-px bg-white/50"
                    style={{ [language === 'ar' ? 'right' : 'left']: `${tick}%` }}
                  >
                    <div className={`absolute -top-4 ${language === 'ar' ? 'right-1/2 translate-x-1/2' : 'left-1/2 -translate-x-1/2'} transform text-[8px] text-[#966f2e]`}>
                      {index + 1}h
                    </div>
                  </div>
                ))}

                {/* Break progress bar - Sapphire Blue */}
                <div
                  className={`absolute top-0 h-full bg-blue-500 transition-all duration-300 ${language === 'ar' ? 'right-0' : 'left-0'}`}
                  style={{ width: `${breakHoursPercentage}%` }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Inspector Statistics Card */}
        <Card className="rounded-2xl shadow-lg overflow-hidden border border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-[#b89256]" />
                <span className="font-medium">
                  {language === 'en'
                    ? 'Inspector Statistics'
                    : 'إحصائيات المفتش'}
                </span>
              </div>
            </div>

            {/* Statistics Grid */}
            <div className="grid grid-cols-2 gap-3">
              {/* Average Time Per Inspection */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-[#b89256]" />
                  <span className="text-xs font-medium text-gray-700">
                    {language === 'en'
                      ? 'Avg. Time Per Inspection'
                      : 'متوسط وقت التفتيش'}
                  </span>
                </div>
                <div className="flex items-baseline">
                  <span className="text-xl font-bold text-gray-800">
                    1h 12m
                  </span>
                  <span className={`text-xs text-[#b89256] ${language === 'ar' ? 'mr-2' : 'ml-2'}`}>
                    -8% vs. team
                  </span>
                </div>
              </div>

              {/* Distance Traveled */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Navigation className="w-4 h-4 text-[#b89256]" />
                  <span className="text-xs font-medium text-gray-700">
                    {language === 'en'
                      ? 'Distance Traveled Today'
                      : 'المسافة المقطوعة اليوم'}
                  </span>
                </div>
                <div className="flex items-baseline">
                  <span className="text-xl font-bold text-gray-800">
                    12.5 km
                  </span>
                  <span className={`text-xs text-gray-500 ${language === 'ar' ? 'mr-2' : 'ml-2'}`}>
                    3 locations
                  </span>
                </div>
              </div>

              {/* Completion Rate */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <svg
                    viewBox="0 0 24 24"
                    className="w-4 h-4 text-[#b89256]"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6L9 17L4 12" />
                  </svg>
                  <span className="text-xs font-medium text-gray-700">
                    {language === 'en'
                      ? 'Completion Rate'
                      : 'معدل الإنجاز'}
                  </span>
                </div>
                <div className="flex items-center">
                  <div className={`flex-1 ${language === 'ar' ? 'ml-2' : 'mr-2'}`}>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#b89256]"
                        style={{ width: '85%' }}
                      ></div>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-gray-800">
                    85%
                  </span>
                </div>
              </div>

              {/* Quality Score */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <svg
                    viewBox="0 0 24 24"
                    className="w-4 h-4 text-[#b89256]"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                  </svg>
                  <span className="text-xs font-medium text-gray-700">
                    {language === 'en'
                      ? 'Quality Score'
                      : 'درجة الجودة'}
                  </span>
                </div>
                <div className="flex items-baseline">
                  <span className="text-xl font-bold text-gray-800">
                    4.7/5.0
                  </span>
                  <span className={`text-xs text-[#b89256] ${language === 'ar' ? 'mr-2' : 'ml-2'}`}>
                    Top 10%
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TrackPage;
